/**
 * Modal.tsx
 *
 * Standard React modal component using HTML/CSS
 * Replaces react-native-web modals for better compatibility
 */
import React from 'react';
import { useTheme } from '../../services/ThemeContext';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  const { currentTheme: colors, fontFamily } = useTheme();

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${colors.background}CC`,
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%',
          maxWidth: 600,
          maxHeight: '80%',
          borderRadius: 20,
          borderWidth: 1,
          borderColor: colors.border,
          borderStyle: 'solid',
          padding: 24,
          backgroundColor: colors.surface,
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          fontFamily: fontFamily,
          fontSize: 'var(--app-font-size)',
        }}
      >
        {title && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            <h2
              style={{
                fontSize: 28,
                fontWeight: 'bold',
                color: colors.primary,
                margin: 0,
              }}
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: colors.primary,
                fontSize: 16,
                fontWeight: '600',
                cursor: 'pointer',
                padding: '4px 8px',
              }}
            >
              Exit
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

interface ScrollableModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  height?: string;
  footer?: React.ReactNode;
}

export const ScrollableModal: React.FC<ScrollableModalProps> = ({ isOpen, onClose, title, children, height, footer }) => {
  const { currentTheme: colors, fontFamily } = useTheme();

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${colors.background}CC`,
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%',
          maxWidth: 600,
          height: height || undefined,
          maxHeight: height ? undefined : '80%',
          borderRadius: 20,
          borderWidth: 1,
          borderColor: colors.border,
          borderStyle: 'solid',
          backgroundColor: colors.surface,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: 24,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
            borderBottomStyle: 'solid',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2
            style={{
              fontSize: 28,
              fontWeight: 'bold',
              color: colors.primary,
              margin: 0,
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primary,
              fontSize: 16,
              fontWeight: '600',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            Exit
          </button>
        </div>
        <div
          style={{
            padding: 24,
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
            fontFamily: fontFamily,
            fontSize: 'var(--app-font-size)',
          }}
        >
          {children}
        </div>
        {footer && (
          <div
            style={{
              padding: 16,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
